// GoalsCard.js - Updated with Won/Lost handlers
import React from "react";
import { api } from "../actions/api";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useQueryClient } from "@tanstack/react-query";
import {
  invalidateLevelQueries,
  invalidateGoalQueries,
} from "../Utils/queryInvalidation";

const GoalsCard = ({
  goalId,
  goalDetailId,
  goalTitle,
  timePeriod,
  isCompleted,
  userGoal,
  onUpdate,
  onViewReflection,
  disableBtn,
  today,
  activeTabIndex, // newly added for state preservation
  weekStart, // original selected week start (Date)
  ...rest
}) => {
  const queryClient = useQueryClient();
  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  const handleToggleAchieve = async (answerId) => {
    try {
      const response = await api.patch(
        `/user/goal-answers/${answerId}/achieve`,
      );
      const message =
        response?.data?.message ||
        "Target achieved status changed successfully";
      toast.success(message);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update target. Please try again.";

      toast.error(errorMessage);
      console.error("Failed to toggle achieved state:", error);
    }
  };

  const handleWonLost = async (isComplete) => {
    try {
      const response = await api.patch(
        `/user/goal/${userGoal.id}/complete?is_complete=${isComplete}`,
      );

      const message =
        isComplete === 1 ? "Goal marked as Won!" : "Goal marked as Lost!";

      toast.success(response?.data?.message || message);

      // Use centralized query invalidation for level-up detection
      invalidateLevelQueries(queryClient);
      invalidateGoalQueries(queryClient);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update goal status. Please try again.";

      toast.error(errorMessage);
      console.error("Failed to update goal completion status:", error);
    }
  };

  const handleOnTrack = async (isOnTrack) => {
    try {
      const response = await api.patch(
        `/user/goal/${userGoal.id}/ontrack?is_ontrack=${isOnTrack}`,
      );

      const message =
        isOnTrack === 1
          ? "Goal marked as On Track!"
          : "Goal marked as Off Track!";

      toast.success(response?.data?.message || message);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update goal tracking status. Please try again.";

      toast.error(errorMessage);
      console.error("Failed to update goal tracking status:", error);
    }
  };

  const handleViewReflectionClick = () => {
    if (onViewReflection && userGoal?.id) {
      onViewReflection(userGoal.id, goalDetailId);
    }
  };

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  return (
    <div
      className={`goals-added-card ${isCompleted ? "h-100" : ""} `}
      style={{
        border: `1.5px solid ${
          userGoal?.show_won_question
            ? userGoal?.is_complete === true
              ? "#00C48C"
              : userGoal?.is_complete === false
                ? "#FF647C"
                : ""
            : userGoal?.show_track_question
              ? userGoal?.is_ontrack === true
                ? "#00C48C"
                : userGoal?.is_ontrack === false
                  ? "#FF647C"
                  : ""
              : ""
        }`,
      }}
    >
      <div className="d-flex justify-content-between p-3 p-md-4">
        <div className="d-flex gap-3">
          <span style={{ color: "#e6ecf1" }} className="text-heading-h3">
            {isCompleted
              ? `${capitalize(timePeriod)} ${capitalize(goalTitle)} Targets`
              : capitalize(timePeriod)}
          </span>
        </div>
        <div className="d-flex journal-admin-actions">
          <Link
            to="/goals-tent"
            state={{
              goalId: goalId,
              goalDetailId: goalDetailId,
              today: today,
              activeTabIndex,
              weekStart: (weekStart || today)?.toISOString?.() || undefined,
            }}
            onClick={(e) => {
              if (disableBtn) {
                e.preventDefault();
                toast.error(
                  "You can only set targets for the current week, not for other weeks.",
                );
              } else if (isCompleted) {
                e.preventDefault();
                toast.error(
                  "You have already set targets for the current week.",
                );
              }
            }}
            className={`goals-target-icons journal-icon d-flex justify-content-center align-items-center ${disableBtn ? "disabled" : ""}`}
            style={disableBtn ? { cursor: "not-allowed" } : {}}
          >
            <img src="/icons/add-ionc.svg" alt="" />
          </Link>
          {/* <Link
            to="#"
            className="goals-target-icons journal-icon d-flex justify-content-center align-items-center"
          >
            <img src="/icons/three-dots.svg" alt="" />
          </Link> */}
        </div>
      </div>

      {isCompleted ? (
        <>
          <div
            className="view-reflection-text text-body-b2"
            data-bs-toggle="modal"
            data-bs-target="#reflectionTitleModal"
            onClick={handleViewReflectionClick}
            style={{ cursor: "pointer" }}
          >
            View full reflection
          </div>
          <div className="d-flex flex-column px-3 px-md-4">
            <div className="tasks-header">
              <h1 className="mb-0">Targets</h1>
              <div
                className="d-flex align-items-center flex-grow-1 justify-content-end"
                style={{ gap: "11px" }}
              >
                <div className="progress-bar-tasks">
                  <div
                    className="progress-bar-task-fill"
                    style={{ width: `${userGoal.progress}`, maxWidth: "100%" }}
                  ></div>
                </div>
                <span className="text-body-b3" style={{ color: "#c8ced3" }}>
                  {userGoal.progress}
                </span>
              </div>
            </div>
            <div className="tasks-details-goals">
              {userGoal.goal_answers.map((answer) => (
                <div
                  key={answer.id}
                  className="d-flex align-items-center"
                  style={{ gap: "14px", minWidth: 0 }}
                >
                  <input
                    className="goals-tasks-radio"
                    type="checkbox"
                    checked={!!answer.achieved}
                    onChange={() => handleToggleAchieve(answer.id)}
                    id={`answer-check-${answer.id}`}
                  />
                  <label
                    className="body-1-default label-san-target text-truncate-ellipsis"
                    htmlFor={`answer-check-${answer.id}`}
                    title={stripHtml(answer.text)}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(answer.text),
                    }}
                  ></label>
                </div>
              ))}
            </div>
          </div>
          <div className="goals-tasks-btn-container">
            {userGoal?.show_won_question ? (
              <>
                <button
                  className="btn-2 btn-1-primary text-body-b1"
                  style={{
                    color: "#e6ecf1",
                    background:
                      userGoal.is_complete !== true ||
                      userGoal.is_complete === null
                        ? "#1b2c3a"
                        : "",
                    border:
                      userGoal.is_complete !== true ||
                      userGoal.is_complete === null
                        ? "1.5px solid var(--Bg-Border, #1b2c3a)"
                        : "",
                  }}
                  disabled={userGoal.is_complete === true}
                  onClick={(e) => {
                    e.currentTarget.disabled = true;
                    handleWonLost(1).finally(() => {
                      if (e.currentTarget) e.currentTarget.disabled = false;
                    });
                  }}
                >
                  <img src="/icons/tick.svg" alt="" /> Won
                </button>
                <button
                  className="btn-2 btn-1-primary text-body-b1"
                  style={{
                    background:
                      userGoal.is_complete !== false ||
                      userGoal.is_complete === null
                        ? "#1b2c3a"
                        : "",
                    border:
                      userGoal.is_complete !== false ||
                      userGoal.is_complete === null
                        ? "1.5px solid var(--Bg-Border, #1b2c3a)"
                        : "",

                    color: "#e6ecf1",
                  }}
                  disabled={userGoal.is_complete === false}
                  onClick={(e) => {
                    e.currentTarget.disabled = true;
                    handleWonLost(0).finally(() => {
                      if (e.currentTarget) e.currentTarget.disabled = false;
                    });
                  }}
                >
                  <img src="/icons/wrong.svg" alt="" /> Lost
                </button>
              </>
            ) : userGoal?.show_track_question ? (
              <>
                <button
                  className="btn-2 btn-1-primary text-body-b1"
                  style={{
                    background:
                      userGoal.is_ontrack !== true ||
                      userGoal.is_ontrack === null
                        ? "#1b2c3a"
                        : "",
                    border:
                      userGoal.is_ontrack !== true ||
                      userGoal.is_ontrack === null
                        ? "1.5px solid var(--Bg-Border, #1b2c3a)"
                        : "",
                    color: "#e6ecf1",
                  }}
                  disabled={userGoal.is_ontrack === true}
                  onClick={(e) => {
                    e.currentTarget.disabled = true;
                    handleOnTrack(1).finally(() => {
                      if (e.currentTarget) e.currentTarget.disabled = false;
                    });
                  }}
                >
                  <img src="/icons/tick.svg" alt="" />
                  {userGoal?.is_ontrack
                    ? "Marked as on track"
                    : "Mark as on track"}
                </button>
                <button
                  className="btn-2 btn-1-primary text-body-b1"
                  style={{
                    background:
                      userGoal.is_ontrack !== false ||
                      userGoal.is_ontrack === null
                        ? "#1b2c3a"
                        : "",
                    border:
                      userGoal.is_ontrack !== false ||
                      userGoal.is_ontrack === null
                        ? "1.5px solid var(--Bg-Border, #1b2c3a)"
                        : "",
                    color: "#e6ecf1",
                  }}
                  disabled={userGoal.is_ontrack === false}
                  onClick={(e) => {
                    e.currentTarget.disabled = true;
                    handleOnTrack(0).finally(() => {
                      if (e.currentTarget) e.currentTarget.disabled = false;
                    });
                  }}
                >
                  <img src="/icons/wrong.svg" alt="" />{" "}
                  {userGoal?.is_ontrack
                    ? "Mark as off track"
                    : "Marked as off track"}{" "}
                </button>
              </>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className="d-flex justify-content-center goals-target-card-text">
            <span
              className="text-body-b2"
              style={{ color: "#d0d8df", opacity: "0.7" }}
            >
              You have not completed your {capitalize(timePeriod)}{" "}
              {capitalize(goalTitle)} Targets.
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default GoalsCard;
